import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import _ from 'underscore';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { Glyphicon, Button, OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withOverflowProp } from '@bingads-webui-react/with-overflow-prop';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { GetNodeClassNames } from '../../../helpers/node-helper';
import { TextSpanWithForwardedRef } from './text-span-with-forwarded-ref';

function getNodeText(scope, classes, node, forwardedRef) {
  // get url for campaign/adgroup nodes
  function getNodeUrl() {
    const { location, processor, allowedKeys } = scope || {};
    const params = new QueryParam(location, null, null, processor, allowedKeys);
    // replace existing campaignId & adGroupId params with new values
    if (node.entityType === 'Campaign') {
      params.append({ campaignId: node.id });
    } else if (node.entityType === 'AdGroup') {
      params.append({ campaignId: node.parentId });
      params.append({ adGroupId: node.id });
    }
    return location && location.pathname ? `${location.pathname}?${params.stringify()}` : null;
  }

  // disable clicking on links, only allow to open in new tabs/windows
  function onClick(e) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
  }

  function getNodeTextImpl() {
    return (
      <TextSpanWithForwardedRef
        classes={classes}
        node={node}
        forwardedRef={forwardedRef}
      />);
  }

  const nodeUrl = getNodeUrl(node, location);
  const nodeText = getNodeTextImpl(classes, node, forwardedRef);

  return nodeUrl
    ? (<a className={classNames([classes.menuItemAnchor, node.isSelected ? classes.selectedNode : ''])} href={nodeUrl} onClick={onClick} ref={forwardedRef}>{nodeText}</a>)
    : nodeText;
}

const CampaignTreeNodeWithTooltip = ({
  classes, node, isTextOverflow, forwardedRef, scope, instrumentation,
}) => {
  const nodeClasses = node.entityType === 'Campaign'
    ? [GetNodeClassNames(node, classes), 'main-menu-tree-node', 'campaign-tree-node']
    : [GetNodeClassNames(node, classes), 'main-menu-tree-node', 'adgroup-tree-node'];

  const selectedClasses = classNames([classes.beforeIcon, 'main-menu-tree-node-selected']);

  const iconContainerClassName = classNames([
    classes.iconContainer,
    classes.statusIconContainer]);

  const iconStatusClassName = node.status === 'Active' ? classes.activeStatusIcon : classes.pausedStatusIcon;

  let campaignGlyphIcon;
  if (node.isExperiment) {
    campaignGlyphIcon = node.status === 'Active' ? 'ba-TestBeakerSolid' : 'ba-TestBeaker';
  } else {
    campaignGlyphIcon = node.status === 'Active' ? 'ba-RadioBullet2' : 'ba-PauseListView12';
  }

  const mainMenuScenario = useMemo(() =>
    instrumentation.mainMenuScenario.create(), [instrumentation]);
  const treeNodeActivity = useMemo(() =>
    mainMenuScenario.treeNodeActivity.create(), [mainMenuScenario]);

  const onClick = useCallback(() => {
    const {
      values: {
        cid, aid, campaignId, adGroupId,
      },
    } = scope;
    const scopeObject = { cid, aid, campaignId, adGroupId };

    setClarityTag({ CampaignTreeNodeClicked: node.entityType });
    treeNodeActivity.trace({
      ...scopeObject,
      id: node.id,
      name: node.name,
      status: node.status,
      entityType: node.entityType,
      campaignType: node.campaignType,
    });
  }, [treeNodeActivity, scope, node]);

  return (
    <OverlayTrigger
      key="top"
      placement="top"
      overlay={
        <Tooltip id="tooltip-top" className={isTextOverflow ? '' : classes.hiddenTooltip}>
          {node.name}
        </Tooltip>
      }
    >
      <Button
        btnStyle="command"
        className={classNames(nodeClasses)}
        key={node.id}
        aria-label={curlyTemplate(node.ariaLabel)({ entityName: _.escape(node.name) })}
        onClick={onClick}
      >
        {node.isSelected && <div className={selectedClasses} />}
        {
          (node.entityType === 'Campaign') &&
          <div className={iconContainerClassName}>
            <Glyphicon
              className={iconStatusClassName}
              glyph={campaignGlyphIcon}
            />
          </div>
        }

        {getNodeText(scope, classes, node, forwardedRef)}

      </Button>
    </OverlayTrigger>
  );
};

CampaignTreeNodeWithTooltip.propTypes = {
  classes: PropTypes.shape({
    baseMenuItem: PropTypes.string.isRequired,
    textContainer: PropTypes.string.isRequired,
    iconContainer: PropTypes.string.isRequired,
    statusIconContainer: PropTypes.string.isRequired,
    pausedTextContainer: PropTypes.string.isRequired,
    activeStatusIcon: PropTypes.string.isRequired,
    pausedStatusIcon: PropTypes.string.isRequired,
    hiddenTooltip: PropTypes.string.isRequired,
  }).isRequired,
  node: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    name: PropTypes.string.isRequired,
    entityType: PropTypes.string.isRequired,
    status: PropTypes.string,
    isSelected: PropTypes.bool,
  }).isRequired,
  isTextOverflow: PropTypes.bool,
  forwardedRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  instrumentation: PropTypes.shape({
    mainMenuScenario: PropTypes.any,
  }).isRequired,
};

CampaignTreeNodeWithTooltip.defaultProps = {
  forwardedRef: null,
  isTextOverflow: false,
};

export default _.compose(
  withInstrumentation,
  withScope,
  withOverflowProp
)(CampaignTreeNodeWithTooltip);
